import * as React from 'react';
import moment from 'moment-timezone';
import _ from 'lodash';

import { useEffect, useState } from 'react';
import { Stack, Label, TextField, Toggle, DefaultButton, IconButton, Dropdown, DatePicker, PrimaryButton } from '@fluentui/react';
import { OkCancelDialog } from '../core/OkCancelDialog';
import { formatDate } from '../../util/Date';
import { Criterion, Rule } from '../../api/insurance.types';
import { RootState } from '../../redux/types';
import { useSelector } from 'react-redux';
import { createRule, deleteRule, updateRule } from '../../api/insurance';

function updateArray<T>(arr: T[], index: number, update: (item: T) => void): T[] {
  const newArray = arr.slice();
  newArray[index] = {
    ...arr[index],
  };
  update(newArray[index]);
  return newArray;
}

export const RuleEdit = ({ original, onDone }: { original: Rule; onDone: () => void }) => {
  const optionKeyPressed = useSelector((state: RootState) => state.ui.optionKeyDepressed);

  const ruleOptions = ['DYNAMIC_PLAN_GEN', 'COST_ESTIMATION', 'MOCK_INS_RESPONSE', 'TAGGING', 'IN_OUT_VERIFY', 'PRICE_CAP', 'CUSTOM_ERROR', 'PRE_REQUEST'].map((s) => ({
    key: s,
    text: s,
  }));

  const [item, setItem] = useState(_.cloneDeep(original));
  const [ruleParameters, setRuleParameters] = useState(_.keys(original.ruleParameters).map((k) => ({ key: k, value: original.ruleParameters[k] })));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setItem(_.cloneDeep(original));
    setRuleParameters(_.keys(original.ruleParameters).map((k) => ({ key: k, value: original.ruleParameters[k] })));
  }, [original]);

  useEffect(() => {
    setItem({
      ...item,
      ruleParameters: ruleParameters.reduce((map, current) => ({ ...map, [current.key]: current.value }), {}),
    });
    // Intentional to not have item in dependencies (re-entrant)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ruleParameters]);

  const isValid = () => {
    return !saving && item.name && item.criteria.length > 0 && !_.isEqual(item, original);
  };

  const save = (clone?: boolean) => {
    setSaving(true);

    const theItem = { ...item };
    if (clone && moment(theItem.beginDate).isBefore(moment())) {
      // if cloning, clamp beginDate to be today or greater.
      theItem.beginDate = moment().utc().format();
    }

    const promise = !clone && theItem.id ? updateRule(theItem) : createRule(theItem);
    promise.then(() => onDone()).finally(() => setSaving(false));
  };

  return (
    <Stack root={{ style: { marginTop: 20 } }} tokens={{ childrenGap: 12 }}>
      <TextField required={true} label="Name" value={item.name} onChange={(ev, v) => setItem({ ...item, name: v! })} />
      <TextField multiline={true} label="Notes" value={item.notes} onChange={(ev, v) => setItem({ ...item, notes: v! })} />
      <Stack horizontal verticalAlign="baseline" tokens={{ childrenGap: 6 }}>
        <DatePicker
          onSelectDate={(date) => setItem({ ...item, beginDate: moment(date).utc().format() })}
          label="Start"
          value={moment(item.beginDate).toDate()}
          isRequired={true}
          allowTextInput={true}
          disableAutoFocus={true}
          formatDate={formatDate}
        />
        <DatePicker
          onSelectDate={(date) => setItem({ ...item, endDate: moment(date).utc().format() })}
          label="End"
          value={moment(item.endDate).toDate()}
          isRequired={true}
          allowTextInput={true}
          disableAutoFocus={true}
          formatDate={formatDate}
        />
        <Toggle checked={item.active} label="Active" onChange={(ev, c) => setItem({ ...item, active: c! })} />
      </Stack>
      <div>
        <Label required={true}>Criteria</Label>
        {item.criteria.map((c, i) => (
          <Stack horizontal verticalAlign="baseline" tokens={{ childrenGap: 6 }} key={i}>
            <TextField style={{ width: 198 }} value={c.attribute} onChange={(ev, v) => setItem({ ...item, criteria: updateArray(item.criteria, i, (u) => (u.attribute = v!)) })} />
            <Dropdown
              style={{ width: 198 }}
              selectedKey={c.matchType}
              options={[
                { key: 'EQUALS', text: 'EQUALS' },
                { key: 'REGEX', text: 'REGEX' },
                { key: 'REGEX_ANY_IN_LIST', text: 'REGEX_ANY_IN_LIST' },
              ]}
              onChange={(ev, o) => setItem({ ...item, criteria: updateArray(item.criteria, i, (u) => (u.matchType = o?.key as string)) })}
            />
            <TextField style={{ width: 300 }} value={c.value} onChange={(ev, v) => setItem({ ...item, criteria: updateArray(item.criteria, i, (u) => (u.value = v!)) })} />
            <Toggle
              checked={c.inverted}
              label="Inverted"
              inlineLabel
              onChange={(ev, c) => setItem({ ...item, criteria: updateArray(item.criteria, i, (u) => (u.inverted = c!)) })}
            />
            <IconButton iconProps={{ iconName: 'Delete' }} onClick={() => setItem({ ...item, criteria: item.criteria.filter((t) => t !== c) })} />
          </Stack>
        ))}
        <IconButton iconProps={{ iconName: 'Add' }} onClick={() => setItem({ ...item, criteria: [...item.criteria, { matchType: 'EQUALS' } as Criterion] })} />
      </div>
      <Dropdown
        style={{ width: 200 }}
        label="Rule"
        selectedKey={item.partitionName}
        options={ruleOptions}
        required={true}
        onChange={(ev, o) => setItem({ ...item, partitionName: o?.key as string })}
      />
      <div>
        <Label>Rule Parameters</Label>
        <Stack tokens={{ childrenGap: 6 }}>
          {ruleParameters.map((c, i) => (
            <Stack horizontal verticalAlign="baseline" tokens={{ childrenGap: 6 }} key={i}>
              <TextField style={{ width: 198 }} value={c.key} onChange={(ev, v) => setRuleParameters(updateArray(ruleParameters, i, (u) => (u.key = v!)))} />
              <TextField style={{ width: 500 }} value={c.value} onChange={(ev, v) => setRuleParameters(updateArray(ruleParameters, i, (u) => (u.value = v!)))} />
              <IconButton iconProps={{ iconName: 'Delete' }} onClick={() => setRuleParameters(ruleParameters.filter((t) => t !== c))} />
            </Stack>
          ))}
          <IconButton iconProps={{ iconName: 'Add' }} onClick={() => setRuleParameters([...ruleParameters, { key: '', value: '' }])} />
        </Stack>
      </div>

      <Stack horizontal={true} tokens={{ childrenGap: 10 }} horizontalAlign="space-between">
        <PrimaryButton text={original.id ? 'Save' : 'Create'} disabled={!isValid()} onClick={() => save()} />
        {original.id && (
          <>
            <DefaultButton text="Save as New" disabled={!isValid()} onClick={() => save(true)} />
            <DefaultButton
              text="Delete"
              onClick={() =>
                OkCancelDialog.show({
                  title: 'Delete rule',
                  subText: `This rule will be deleted and cannot be undone.  Are you sure you want to delete ${original.name} rule?`,
                  onOk: () => {
                    deleteRule(item.id).then(() => onDone());
                  },
                })
              }
            />
          </>
        )}
      </Stack>

      {optionKeyPressed && (
        <div>
          <Label>Diagnostics (original vs. edited)</Label>
          <Stack horizontal={true}>
            <Stack.Item grow={1} style={{ width: 0 }}>
              <pre style={{ margin: 0, padding: 12, overflow: 'auto', background: '#eaeaea' }}>{JSON.stringify(original, null, 2)}</pre>
            </Stack.Item>
            <Stack.Item grow={1} style={{ width: 0 }}>
              <pre style={{ margin: 0, padding: 12, overflow: 'auto', background: '#efefef' }}>{JSON.stringify(item, null, 2)}</pre>
            </Stack.Item>
          </Stack>
        </div>
      )}
    </Stack>
  );
};
